import { formatArticleDate } from '@archetelos/lib/date';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

import { useTrackView } from '@/features/analytics/useTrackView';
import { decideWatch } from '@/features/browse/gate';
import { MenuBar } from '@/features/browse/MenuBar';
import { TopBar } from '@/features/browse/TopBar';
import { VideoCard } from '@/features/browse/VideoCard';
import { PlayerSurface, type PlaybackPayload } from '@/features/watch/PlayerSurface';
import { useAuth } from '@/lib/auth/useAuth';
import { FREE_PLAY_LIMIT, freemiumQueries } from '@/lib/queries/freemium';
import { subscriberQueries, upsertWatchProgress } from '@/lib/queries/subscriber';
import { watchQueries } from '@/lib/queries/watch';
import { supabase } from '@/lib/supabase';

// The watch page — /videos/{slug}. Replaces the redirect stub in place; the URL
// contract is a marketing link shape and stays. The four gate states derive from
// freemiumQueries.status + decideWatch (UI only, fail-open while loading); the
// video-playback-token function is the authoritative wall — no fetchable stream
// ever reaches a viewer the server hasn't cleared.
export const Route = createFileRoute('/videos/$slug')({
  component: WatchRoute,
});

function WatchChrome({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-video-bg text-video-text">
      <TopBar />
      <MenuBar />
      {children}
    </div>
  );
}

function WatchRoute() {
  const { slug } = Route.useParams();
  const { session } = useAuth();
  const signedIn = Boolean(session);
  const queryClient = useQueryClient();

  const { data: video, isLoading } = useQuery(watchQueries.render(slug));
  const { data: status } = useQuery(freemiumQueries.status(signedIn));
  const { data: rail } = useQuery({
    ...watchQueries.rail(video?.categoryId ?? null, video?.id ?? ''),
    enabled: Boolean(video),
  });
  // Saved resume row for this video (signed-in only; anon never resumes). Own-row RLS.
  const { data: savedProgress } = useQuery(
    subscriberQueries.watchProgressFor(session?.user.id, video?.id),
  );

  const [playback, setPlayback] = useState<PlaybackPayload | null>(null);
  const [starting, setStarting] = useState(false);
  const [playError, setPlayError] = useState(false);

  // Anonymous lane-1 view — no id, even for a signed-in viewer (Spec 22).
  useTrackView(video?.id);

  // Lane-2 position tracking (Increment 2). latestPos holds the most recent
  // timeupdate; lastWriteAt gates the ~15s throttle. session/video refs let the
  // unmount flush read current values without re-running its effect.
  const latestPos = useRef<{ seconds: number; duration: number } | null>(null);
  const lastWriteAt = useRef(0);
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const videoRef = useRef(video);
  videoRef.current = video;

  // Flush the last known position on unmount — the throttle can swallow the final
  // tick, and leaving mid-video is exactly when resume matters most.
  useEffect(() => {
    return () => {
      const s = sessionRef.current;
      const vid = videoRef.current;
      const p = latestPos.current;
      if (s && vid && p && p.duration > 0) {
        void upsertWatchProgress({
          userId: s.user.id,
          contentId: vid.id,
          creatorId: vid.creatorId,
          positionSeconds: p.seconds,
          durationSeconds: p.duration,
        });
      }
    };
  }, []);

  if (isLoading) {
    return (
      <WatchChrome>
        <p className="py-20 text-center font-sans text-sm text-video-text-faint">Loading…</p>
      </WatchChrome>
    );
  }
  if (!video) {
    return (
      <WatchChrome>
        <div className="mx-auto max-w-[860px] px-[18px] py-20 text-center">
          <h1 className="font-serif text-2xl text-video-text">This video isn&apos;t available.</h1>
          <p className="mt-2 font-sans text-sm text-video-text-muted">
            It may have been unpublished, or the link is wrong.
          </p>
          <a href="/" className="mt-6 inline-block font-sans text-sm text-silver hover:underline">
            Back to browse
          </a>
        </div>
      </WatchChrome>
    );
  }

  const isMember = status?.isMember ?? false;
  const unlocked = status?.unlockedIds.includes(video.id) ?? false;
  const remaining = status?.remaining ?? 0;
  // Fail-open governs the UI only (show "Watch" while status loads); the function
  // remains the wall.
  const decision =
    signedIn && !status
      ? 'play'
      : decideWatch({ kind: 'vod', signedIn, isMember, unlocked, remaining });
  // The spend line shows only for a signed-in free member on a locked title with
  // plays left — never for members or an already-unlocked (free rewatch) title.
  const showSpendLine = decision === 'play' && !isMember && !unlocked && remaining > 0;
  const signInHref = `/auth/sign-in?next=${encodeURIComponent(`/videos/${slug}`)}`;

  // Resume only an unfinished row with real progress; the -5s clamp keeps a
  // near-end position from resuming into the credits. A completed video reopens
  // at 0 (a rewatch, not a resume).
  const resumeAt =
    savedProgress &&
    !savedProgress.completed &&
    savedProgress.position_seconds > 0 &&
    savedProgress.duration_seconds != null &&
    savedProgress.position_seconds < savedProgress.duration_seconds - 5
      ? savedProgress.position_seconds
      : undefined;

  // Throttled lane-2 position write. immediate=true bypasses the throttle for the
  // start-row and the ended-write.
  const writeProgress = (seconds: number, duration: number | undefined, immediate: boolean) => {
    if (!session) return;
    if (duration != null) latestPos.current = { seconds, duration };
    const now = Date.now();
    if (!immediate && now - lastWriteAt.current < 15000) return;
    lastWriteAt.current = now;
    void upsertWatchProgress({
      userId: session.user.id,
      contentId: video.id,
      creatorId: video.creatorId,
      positionSeconds: seconds,
      durationSeconds: duration,
    });
  };
  // Re-wired Increment-1 start-write: an immediate row on play, at the best-known
  // position (a resume seek or a mid-play pause→resume) so it never clobbers a
  // real position back to 0.
  const handlePlay = () => {
    const p = latestPos.current;
    writeProgress(p?.seconds ?? resumeAt ?? 0, p?.duration, true);
  };
  const handleEnded = () => {
    const p = latestPos.current;
    if (p && p.duration > 0) writeProgress(p.duration, p.duration, true);
  };

  const startWatch = async () => {
    setStarting(true);
    setPlayError(false);
    try {
      const { data, error } = await supabase.functions.invoke('video-playback-token', {
        body: { contentId: video.id },
      });
      const payload = data as { allowed?: boolean; playback?: PlaybackPayload } | null;
      if (error || !payload?.allowed || !payload.playback) {
        // A genuine out_of_plays deny comes back as a 403; refetch status so the
        // reactive gate swaps to the wall (no retry note). Any other failure is
        // transient and worth a soft retry note.
        await queryClient.invalidateQueries({ queryKey: ['freemium', 'status'] });
        const httpStatus = (error as { context?: { status?: number } } | null)?.context?.status;
        if (error && httpStatus !== 403) setPlayError(true);
        return;
      }
      setPlayback(payload.playback);
      // The play was consumed server-side; refresh the pill + gate.
      await queryClient.invalidateQueries({ queryKey: ['freemium', 'status'] });
    } catch {
      setPlayError(true);
    } finally {
      setStarting(false);
    }
  };

  return (
    <WatchChrome>
      <div className="mx-auto max-w-[860px] px-[18px] py-6">
        <div className="relative aspect-video overflow-hidden rounded-lg bg-video-inset ring-1 ring-video-line">
          {playback ? (
            <PlayerSurface
              playback={playback}
              title={video.title}
              // Lane-2 resume writes — signed-in only; anon watchers write nothing
              // here (their lane-1 view already fired via useTrackView). onPlay is
              // the immediate start-row, onTimeUpdate the throttled position, onEnded
              // the completion write; resumeAt seeks the player back on reopen.
              onPlay={session ? handlePlay : undefined}
              onTimeUpdate={session ? (s, d) => writeProgress(s, d, false) : undefined}
              onEnded={session ? handleEnded : undefined}
              resumeAt={resumeAt}
            />
          ) : (
            <>
              {video.coverImageUrl && (
                <img
                  src={video.coverImageUrl}
                  alt={video.coverImageAlt ?? ''}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              )}
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[55%] bg-gradient-to-t from-video-bg/95 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-5 text-center">
                {decision === 'signin' && (
                  <a
                    href={signInHref}
                    className="inline-block rounded-md bg-video-text px-6 py-2.5 font-sans text-sm font-medium text-video-bg transition hover:opacity-90"
                  >
                    Sign in to watch
                  </a>
                )}
                {decision === 'upgrade' && (
                  <div className="space-y-3">
                    <p className="mx-auto max-w-[34ch] font-serif text-base text-video-text">
                      You&apos;ve used your {FREE_PLAY_LIMIT} free plays this month — they reset on
                      the 1st. Subscribe to keep watching.
                    </p>
                    <a
                      href="/subscribe"
                      className="inline-block rounded-md bg-oxblood px-6 py-2.5 font-sans text-sm font-medium text-video-text transition hover:opacity-90"
                    >
                      Subscribe
                    </a>
                  </div>
                )}
                {decision === 'play' && (
                  <div className="space-y-2">
                    {showSpendLine && (
                      <p className="font-sans text-xs text-video-text-muted">
                        This will use one of your {remaining} free plays this month.
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={() => void startWatch()}
                      disabled={starting}
                      className="inline-block rounded-md bg-video-text px-6 py-2.5 font-sans text-sm font-medium text-video-bg transition hover:opacity-90 disabled:opacity-60"
                    >
                      {starting ? 'Starting…' : '▶ Watch'}
                    </button>
                    {playError && (
                      <p className="font-sans text-xs text-signal">
                        Couldn&apos;t start playback. Try again.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="mt-4">
          <h1 className="font-serif text-2xl text-video-text">{video.title}</h1>
          <p className="mt-1.5 font-sans text-xs text-video-text-muted">
            By{' '}
            {video.creatorUsername ? (
              <Link
                to="/subscriber/creators/$username"
                params={{ username: video.creatorUsername }}
                className="text-silver hover:underline"
              >
                {video.creatorName}
              </Link>
            ) : (
              <span className="text-silver">{video.creatorName}</span>
            )}
            {video.publishedAt && <> · {formatArticleDate(new Date(video.publishedAt))}</>}
          </p>
          {video.excerpt && (
            <p className="mt-3 max-w-[70ch] font-sans text-sm text-video-text-muted">
              {video.excerpt}
            </p>
          )}
        </div>

        {rail && rail.videos.length > 0 && (
          <section className="mt-8">
            <h2 className="mb-3 font-sans text-sm font-medium uppercase tracking-[0.04em] text-video-text">
              {rail.heading}
            </h2>
            <div className="grid grid-cols-3 gap-4">
              {rail.videos.map((v) => (
                <VideoCard key={v.id} video={v} />
              ))}
            </div>
          </section>
        )}
      </div>
    </WatchChrome>
  );
}
